/**
 * Phone and email OTP authentication
 * ------------------------------------------------------------------
 * Drives the sign-in screen (Figma 1159:1651 / 3017:2665) and the Phone
 * Verification card on the new-user checkout (1238:1740 / 3017:2441).
 *
 * Liquid cannot send an SMS or mint a session, so the theme is deliberately
 * only the front half. The back half is the custom profile app, reached
 * through assets/pf-api.js — which owns the base URL, the credential mode and
 * the error envelope, so this file only has to know the two endpoints:
 *
 *   POST <base>/v1/auth/{phone|email}/start
 *   POST <base>/v1/auth/{phone|email}/verify
 *
 * Verify replies with a Set-Cookie that logs the shopper into the app. It does
 * NOT create a native Shopify customer session — the store is on Basic, so
 * there is no multipass to mint one — which is why the profile surfaces read
 * their own identity from the app (assets/pf-profile-session.js) instead of
 * Liquid's `customer` object. Phone verification uses a top-level POST on both
 * signup and checkout, so browser cookie restrictions cannot create a
 * storefront-only identity that differs from the shared RBY session.
 *
 * With no base configured every call resolves to NOT_CONNECTED and the screen
 * says so, which is the honest state until the app is deployed.
 *
 * Right by You consumes this same pf_session. No second RBY or Shopify
 * customer-account authentication is created.
 */
(function () {
  "use strict";

  var RESEND_SECONDS = 30;
  var cachedStrings;

  function strings() {
    if (cachedStrings) return cachedStrings;
    var node = document.querySelector("[data-pf-auth-strings]");
    try {
      cachedStrings = node ? JSON.parse(node.textContent) : {};
    } catch (error) {
      cachedStrings = {};
    }
    return cachedStrings;
  }

  function message(key, fallback) {
    return strings()[key] || fallback;
  }

  function setStatus(root, message, isError) {
    var status = root.querySelector("[data-pf-auth-status]");
    if (!status) return;
    status.textContent = message || "";
    status.hidden = !message;
    status.classList.toggle("is-error", !!isError);
  }

  function showVerifiedPhone(root, person) {
    root.querySelectorAll("[data-pf-auth-challenge]").forEach(function (node) {
      node.hidden = true;
    });
    var known = root.querySelector("[data-pf-auth-known]");
    var label = root.querySelector("[data-pf-auth-known-phone]");
    if (known) known.hidden = false;
    if (label) {
      label.textContent = (
        root.getAttribute("data-pf-auth-verified-template") ||
        "Verified phone ending {last4}"
      ).replace("{last4}", person.phone.slice(-4));
    }
    root.classList.add("is-verified");
    var verified = root.querySelector("[data-pf-auth-verified]");
    if (verified) verified.value = person.phone;
    document.querySelectorAll('[data-pf-co-field="phone"]').forEach(function (input) {
      if (!input.value.trim()) fillPhone(input, person.phone);
    });
  }

  function api() {
    return window.PFApi || null;
  }

  function notConnectedMessage() {
    var client = api();
    return client
      ? client.NOT_CONNECTED_MESSAGE
      : "This is not connected yet. Please try again later.";
  }

  function post(path, payload) {
    var client = api();
    if (!client) {
      return Promise.reject(new Error(notConnectedMessage()));
    }
    return client.post(path, payload);
  }

  function requestedReturnPath() {
    try {
      return new URLSearchParams(window.location.search).get("return_to") || "";
    } catch (error) {
      return "";
    }
  }

  /**
   * Where a completed sign-in goes. The screen names its own destination in
   * data-pf-auth-success-url, and the profile guard
   * (assets/pf-profile-session.js) appends ?return_to= when it sends a
   * signed-out shopper here, so people land back on the page they asked for
   * rather than always on the hub. Same-origin paths and the one explicitly
   * configured RBY app origin are honoured; everything else is rejected.
   */
  function successUrl(root) {
    var declared = root.getAttribute("data-pf-auth-success-url");
    if (!declared) return "";

    /* The profile guard uses same-origin paths. RBY runs on PF's separate
       first-party app subdomain, so that exact configured origin is also safe. */
    var requested = requestedReturnPath();
    if (requested.charAt(0) === "/") {
      try {
        var local = new URL(requested, window.location.origin);
        if (local.origin === window.location.origin)
          return local.pathname + local.search + local.hash;
      } catch (error) {}
    }
    try {
      var requestedUrl = new URL(requested);
      var allowedRbyOrigin = root.getAttribute("data-pf-auth-rby-origin") || "";
      if (
        allowedRbyOrigin &&
        requestedUrl.origin === new URL(allowedRbyOrigin).origin
      ) {
        return requestedUrl.toString();
      }
    } catch (error) {
      /* Invalid/relative values fall back to the declared profile page. */
    }

    return declared;
  }

  /* Phone sign-in is for Indian mobile numbers only. The field shows a fixed
     +91 and holds the 10-digit number (assets/pf-indian-phone.js keeps it that
     way); the app receives +91 followed by those digits. */
  function phoneInput(root) {
    return root.querySelector("[data-pf-auth-phone]");
  }

  function fullPhone(root) {
    var input = phoneInput(root);
    var raw = input ? input.value.trim() : "";
    if (!raw) return "";
    return window.PFIndianPhone ? window.PFIndianPhone.canonical(raw) : "+91" + raw;
  }

  function phoneProblem(root) {
    var input = phoneInput(root);
    var raw = input ? input.value.trim() : "";
    if (window.PFIndianPhone) return window.PFIndianPhone.error(raw);
    return /^[6-9]\d{9}$/.test(raw)
      ? ""
      : message("phoneInvalid", "Enter a valid 10-digit Indian mobile number.");
  }

  function fillPhone(input, value) {
    if (window.PFIndianPhone) window.PFIndianPhone.fill(input, value);
    else input.value = String(value || "").replace(/^\+91/, "");
  }

  function emailAddress(root) {
    var input = root.querySelector("[data-pf-auth-email]");
    return input ? input.value.trim().toLowerCase() : "";
  }

  function authMode(root) {
    return root.__pfAuthMode === "email" ? "email" : "phone";
  }

  function setMode(root, mode, focus) {
    // Sign-in screen with its phone panel left out (Theme settings → phone
    // sign-in off) is email only. Checkout's phone card has no panels at all.
    var phoneOff =
      !root.querySelector('[data-pf-auth-panel="phone"]') &&
      !!root.querySelector('[data-pf-auth-panel="email"]');
    mode = mode === "email" || phoneOff ? "email" : "phone";
    root.__pfAuthMode = mode;
    root.querySelectorAll("[data-pf-auth-panel]").forEach(function (panel) {
      panel.hidden = panel.getAttribute("data-pf-auth-panel") !== mode;
    });
    var emailTile = root.querySelector('[data-pf-auth-provider="email"]');
    if (emailTile) {
      emailTile.classList.toggle("is-active", mode === "email");
      emailTile.setAttribute(
        "aria-expanded",
        mode === "email" ? "true" : "false",
      );
    }
    var submit = root.querySelector("[data-pf-auth-submit]");
    if (submit) {
      var attribute =
        mode === "email"
          ? "data-pf-auth-email-label"
          : "data-pf-auth-phone-label";
      submit.textContent =
        submit.getAttribute(attribute) ||
        message(
          mode === "email" ? "emailSubmit" : "phoneSubmit",
          mode === "email" ? "Continue with email" : "Create account / Login",
        );
    }
    setStatus(root, "");
    if (focus) {
      var input = root.querySelector(
        mode === "email" ? "[data-pf-auth-email]" : "[data-pf-auth-phone]",
      );
      if (input) input.focus();
    }
  }

  function startCooldown(button, root, seconds) {
    var label = button.getAttribute("data-pf-auth-label") || button.textContent;
    button.setAttribute("data-pf-auth-label", label);
    var remaining = Math.max(1, parseInt(seconds, 10) || RESEND_SECONDS);
    button.disabled = true;
    button.textContent = message("resend", "Resend") + " " + remaining + "s";

    window.clearInterval(button.__pfAuthTimer);
    button.__pfAuthTimer = window.setInterval(function () {
      remaining -= 1;
      if (remaining <= 0) {
        window.clearInterval(button.__pfAuthTimer);
        button.__pfAuthTimer = null;
        button.disabled = false;
        button.textContent = label;
        return;
      }
      button.textContent = message("resend", "Resend") + " " + remaining + "s";
    }, 1000);
  }

  function sendOtp(root, button) {
    if (button.disabled) return;
    var mode = authMode(root);
    var credential = mode === "email" ? emailAddress(root) : fullPhone(root);
    if (mode === "phone" && phoneProblem(root)) {
      setStatus(root, phoneProblem(root), true);
      return;
    }
    if (mode === "email" && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(credential)) {
      setStatus(
        root,
        message("emailInvalid", "Enter a valid email address."),
        true,
      );
      return;
    }

    var label = button.textContent;
    button.disabled = true;
    button.textContent = message("sending", "Sending…");
    setStatus(root, "");

    post(
      "/v1/auth/" + mode + "/start",
      mode === "email" ? { email: credential } : { phone: credential },
    )
      .then(function (body) {
        setStatus(
          root,
          message(
            mode === "email" ? "codeSentEmail" : "codeSentPhone",
            mode === "email"
              ? "Check your email for your sign-in code."
              : "Code sent to your phone.",
          ),
        );
        var panel =
          root.querySelector('[data-pf-auth-panel="' + mode + '"]') || root;
        var code = panel.querySelector("[data-pf-auth-code]");
        if (code) code.focus();
        button.textContent = label;
        // The app decides how long the shopper must wait, and answers 429
        // with the same figure if they ask again too early.
        startCooldown(button, root, body.resendAfterSec);
      })
      .catch(function (error) {
        button.disabled = false;
        button.textContent = label;
        setStatus(root, error.message, true);
        if (error.code === "RESEND_COOLDOWN" || error.code === "RATE_LIMITED") {
          startCooldown(button, root, error.retryAfterSec);
        }
      });
  }

  function verifyOtp(root, button) {
    if (button.disabled) return;
    var mode = authMode(root);
    var credential = mode === "email" ? emailAddress(root) : fullPhone(root);
    var panel =
      root.querySelector('[data-pf-auth-panel="' + mode + '"]') || root;
    var codeInput = panel.querySelector("[data-pf-auth-code]");
    var code = codeInput ? codeInput.value.trim() : "";

    if (mode === "phone" && phoneProblem(root)) {
      setStatus(root, phoneProblem(root), true);
      return;
    }
    if (mode === "email" && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(credential)) {
      setStatus(
        root,
        message("emailInvalid", "Enter a valid email address."),
        true,
      );
      return;
    }
    if (!/^\d{6}$/.test(code)) {
      setStatus(
        root,
        message("codeInvalid", "Enter the six-digit code we sent you."),
        true,
      );
      if (codeInput) codeInput.focus();
      return;
    }

    var label = button.textContent;
    button.disabled = true;
    button.textContent = message("verifying", "Verifying…");
    setStatus(root, "");

    var client = api();
    if (!client || !client.isConfigured()) {
      button.disabled = false;
      button.textContent = label;
      setStatus(root, notConnectedMessage(), true);
      return;
    }
    document.dispatchEvent(new CustomEvent("pf:auth:leaving"));
    var returnTo = new URL(
      successUrl(root) || location.pathname + location.search,
      location.origin,
    ).toString();
    var form = document.createElement("form");
    form.method = "POST";
    form.action = client.base() + "/v1/auth/" + mode + "/complete";
    var values = { code: code, return_to: returnTo };
    values[mode] = credential;
    Object.keys(values).forEach(function (name) {
      var field = document.createElement("input");
      field.type = "hidden";
      field.name = name;
      field.value = values[name];
      form.appendChild(field);
    });
    document.body.appendChild(form);
    form.submit();
  }

  function initRoot(root) {

    if (!root || root.__pfAuthBound) return;
    root.__pfAuthBound = true;
    // A rejected keystroke or paste in the phone field reports on this
    // screen's own status line.
    root.addEventListener("pf:phone-india-message", function (event) {
      if (!event.target.matches("[data-pf-auth-phone]")) return;
      event.preventDefault();
      setStatus(root, event.detail && event.detail.message, true);
    });
    var params = new URLSearchParams(location.search);
    var authError = params.get("pf_auth_error");
    setMode(
      root,
      params.get("auth") === "email" || authError === "invalid_email_otp"
        ? "email"
        : "phone",
    );
    if (authError)
      setStatus(
        root,
        authError === "invalid_otp" || authError === "invalid_email_otp"
          ? message(
              "invalidCode",
              "That code did not work. Send a new code and try again.",
            )
          : message(
              "signinFailed",
              "Sign-in could not be completed. Please try again.",
            ),
        true,
      );

    // Provider tiles render even without an OAuth start URL so the screen
    // matches the frame; an unconfigured one says so instead of dead-ending.
    root.querySelectorAll("[data-pf-auth-provider]").forEach(function (tile) {
      tile.addEventListener("click", function () {
        if (tile.getAttribute("data-pf-auth-provider") === "email") {
          setMode(root, "email", true);
          return;
        }
        if (tile.tagName === "A") {
          if (api() && api().resetSessionBridge) api().resetSessionBridge();
          var returnTo = successUrl(root);
          if (returnTo) {
            try {
              var providerUrl = new URL(tile.href);
              providerUrl.searchParams.set(
                "return_to",
                new URL(returnTo, window.location.origin).toString(),
              );
              tile.href = providerUrl.toString();
            } catch (error) {
              /* Keep the authored provider URL when it cannot be parsed. */
            }
          }
          return;
        }
        setStatus(
          root,
          message(
            "providerUnavailable",
            "Sign-in is not connected yet. Use phone or email sign-in.",
          ),
          true,
        );
      });
    });

    root.querySelectorAll("[data-pf-auth-send]").forEach(function (send) {
      send.addEventListener("click", function (event) {
        event.preventDefault();
        sendOtp(root, send);
      });
    });

    var usePhone = root.querySelector("[data-pf-auth-use-phone]");
    if (usePhone)
      usePhone.addEventListener("click", function () {
        setMode(root, "phone", true);
      });

    var submit = root.querySelector("[data-pf-auth-submit]");
    if (submit) {
      submit.addEventListener("click", function (event) {
        event.preventDefault();
        verifyOtp(root, submit);
      });
    }

    // Checkout verification also establishes the same first-party session used by RBY.
    if (root.closest("[data-pf-co-root]") && api() && api().isConfigured()) {
      api()
        .get("/v1/auth/session")
        .then(function (body) {
          if (!body || !body.authenticated) return null;
          return api().get("/v1/me");
        })
        .then(function (body) {
          var person = body && body.profile;
          if (!person || !person.phoneVerified || !person.phone) return;
          var input = root.querySelector("[data-pf-auth-phone]"),
            verified = root.querySelector("[data-pf-auth-verified]");
          if (input) fillPhone(input, person.phone);
          if (verified) verified.value = person.phone;
          showVerifiedPhone(root, person);
          if (submit) {
            submit.disabled = true;
            submit.textContent = "Verified";
          }
          setStatus(root, "");
          function edited() {
            if (fullPhone(root) === person.phone) return;
            root.classList.remove("is-verified");
            if (verified) verified.value = "";
            if (submit) {
              submit.disabled = false;
              submit.textContent = "Verify Phone";
            }
            setStatus(root, "");
          }
          root
            .querySelectorAll("[data-pf-auth-phone]")
            .forEach(function (node) {
              node.addEventListener("input", edited);
              node.addEventListener("change", edited);
            });
        })
        .catch(function () {});
    }

    root.querySelectorAll("[data-pf-auth-code]").forEach(function (code) {
      if (!submit) return;
      code.addEventListener("keydown", function (event) {
        if (event.key !== "Enter") return;
        event.preventDefault();
        submit.click();
      });
    });
  }

  function boot() {
    document.querySelectorAll("[data-pf-auth]").forEach(initRoot);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot, { once: true });
  } else {
    boot();
  }

  document.addEventListener("shopify:section:load", function (event) {
    var root = event.target.querySelector("[data-pf-auth]");
    if (root) initRoot(root);
  });
})();
